import { useState, useEffect, useCallback } from 'react';

export interface TimeRemaining {
  hours: number;
  minutes: number;
  seconds: number;
  totalSeconds: number;
  isExpired: boolean;
  formattedTime: string;
}

export interface UseCheckoutTimerProps {
  expiresAt?: string | null;
  isCheckedOut?: boolean;
  onExpired?: () => void;
}

/**
 * Custom hook for checkout timer functionality
 * Calculates remaining time and provides real-time updates
 */
export function useCheckoutTimer({ 
  expiresAt, 
  isCheckedOut = false, 
  onExpired 
}: UseCheckoutTimerProps): TimeRemaining {
  
  const [timeRemaining, setTimeRemaining] = useState<TimeRemaining>({
    hours: 0,
    minutes: 0,
    seconds: 0,
    totalSeconds: 0,
    isExpired: false,
    formattedTime: '0h 0m 0s'
  });

  const calculateTimeRemaining = useCallback((): TimeRemaining => {
    if (!expiresAt || !isCheckedOut) {
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isExpired: false,
        formattedTime: '0h 0m 0s'
      };
    }

    const now = new Date();
    const expiration = new Date(expiresAt);
    const remainingMs = expiration.getTime() - now.getTime();

    if (remainingMs <= 0) {
      return {
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isExpired: true,
        formattedTime: 'Expired'
      };
    }

    const totalSeconds = Math.floor(remainingMs / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    // Format time display
    let formattedTime: string;
    if (hours > 0) {
      formattedTime = `${hours}h ${minutes}m`;
    } else if (minutes > 0) {
      formattedTime = `${minutes}m ${seconds}s`;
    } else {
      formattedTime = `${seconds}s`;
    }

    return {
      hours,
      minutes,
      seconds,
      totalSeconds,
      isExpired: false,
      formattedTime
    };
  }, [expiresAt, isCheckedOut]);

  // Update timer every second
  useEffect(() => {
    if (!expiresAt || !isCheckedOut) {
      setTimeRemaining({
        hours: 0,
        minutes: 0,
        seconds: 0,
        totalSeconds: 0,
        isExpired: false,
        formattedTime: '0h 0m 0s'
      });
      return;
    }

    // Initial calculation
    const initialTime = calculateTimeRemaining();
    setTimeRemaining(initialTime);

    // Set up interval for updates
    const interval = setInterval(() => {
      const newTime = calculateTimeRemaining();
      setTimeRemaining(newTime);

      // Call onExpired callback when timer expires
      if (newTime.isExpired && onExpired) {
        onExpired();
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [expiresAt, isCheckedOut, calculateTimeRemaining, onExpired]);

  return timeRemaining;
}

/**
 * Utility function to get color class based on remaining time
 */
export function getTimerColorClass(timeRemaining: TimeRemaining): string {
  if (timeRemaining.isExpired) {
    return 'text-red-600 dark:text-red-400';
  }
  
  if (timeRemaining.totalSeconds < 300) { // < 5 minutes
    return 'text-red-600 dark:text-red-400';
  }
  
  if (timeRemaining.totalSeconds < 1800) { // < 30 minutes
    return 'text-orange-600 dark:text-orange-400';
  }
  
  return 'text-green-600 dark:text-green-400';
}

/**
 * Utility function to get urgency indicator
 */
export function getTimerUrgency(timeRemaining: TimeRemaining): 'expired' | 'critical' | 'warning' | 'normal' {
  if (timeRemaining.isExpired) return 'expired';
  if (timeRemaining.totalSeconds < 300) return 'critical'; // < 5 minutes
  if (timeRemaining.totalSeconds < 1800) return 'warning'; // < 30 minutes
  return 'normal';
}
